import { useApolloClient } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';

import { CREATE_PERSON, errorCode, ME, PERSON_EXISTS } from '@/account/arcade-api';
import { useAuth } from '@/auth/useAuth';
import { AuthScreen } from '@/components/AuthScreen/AuthScreen';
import {
  CreateAccountForm,
  type AccountDetails,
} from '@/components/CreateAccountForm/CreateAccountForm';
import { SignInForm } from '@/components/SignInForm/SignInForm';
import { environmentName } from '@/config/runtime-config';
import { useToast } from '@/toast/useToast';

type Stage = 'email' | 'details' | 'pin';
type Purpose = 'signin' | 'signup';

/**
 * Owns the sign-in and sign-up conversation. An email is checked against
 * Arcade first: a known one gets a pin, an unknown one the account form. On
 * sign-up the Person is created with the token the pin exchange returns,
 * before the app sees the identity, so it lands on a finished account.
 */
export function SignInContainer() {
  const { requestPin, submitPin } = useAuth();
  const client = useApolloClient();
  const toast = useToast();
  const environment = useMemo(() => environmentName(), []);
  const [stage, setStage] = useState<Stage>('email');
  const [purpose, setPurpose] = useState<Purpose>('signin');
  const [email, setEmail] = useState('');
  const [details, setDetails] = useState<AccountDetails | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const hasAccount = useCallback(
    async (address: string) => {
      const { data } = await client.query({
        query: PERSON_EXISTS,
        variables: { email: address },
        fetchPolicy: 'network-only',
      });
      return data?.personExists ?? false;
    },
    [client],
  );

  const sendPin = useCallback(
    async (address: string, next: Purpose) => {
      setChallengeId(await requestPin(address));
      setEmail(address);
      setPurpose(next);
      setStage('pin');
      setNotice(null);
    },
    [requestPin],
  );

  const run = useCallback(async (work: () => Promise<void>, failure: string) => {
    setPending(true);
    setError(null);
    try {
      await work();
    } catch {
      setError(failure);
    } finally {
      setPending(false);
    }
  }, []);

  const onRequestPin = useCallback(
    (address: string) =>
      run(async () => {
        if (await hasAccount(address)) {
          await sendPin(address, 'signin');
          return;
        }
        setEmail(address);
        setNotice(`There is no developer account for ${address} yet. Create one below.`);
        setStage('details');
      }, 'Could not request a pin. Check the address and try again.'),
    [run, hasAccount, sendPin],
  );

  const onDetails = useCallback(
    (entered: AccountDetails) =>
      run(async () => {
        setDetails(entered);
        if (await hasAccount(entered.email)) {
          toast.info({
            title: 'You already have an account',
            description: 'Enter the pin we sent to sign in.',
          });
          await sendPin(entered.email, 'signin');
          return;
        }
        await sendPin(entered.email, 'signup');
      }, 'Could not request a pin. Check the address and try again.'),
    [run, hasAccount, sendPin, toast],
  );

  const createAccount = useCallback(
    async (token: string) => {
      if (!details) {
        return;
      }
      try {
        const { data } = await client.mutate({
          mutation: CREATE_PERSON,
          variables: {
            email: details.email,
            firstName: details.firstName,
            lastName: details.lastName,
            businessName: details.businessName,
          },
          context: { headers: { Authorization: `Bearer ${token}` } },
        });
        if (data) {
          client.writeQuery({ query: ME, data: { me: data.createPerson } });
        }
      } catch (failure) {
        if (errorCode(failure) !== 'PERSON_EXISTS') {
          toast.error({
            title: 'Your account was not created',
            description: 'Your email is confirmed. Check your details and try again.',
          });
        }
      }
    },
    [client, details, toast],
  );

  const onSubmitPin = useCallback(
    (pin: string) => {
      if (!challengeId) {
        return;
      }
      return run(async () => {
        if (purpose === 'signup') {
          await submitPin(challengeId, pin, createAccount);
        } else {
          await submitPin(challengeId, pin);
        }
      }, 'That pin was not accepted. Request a new one if it has expired.');
    },
    [challengeId, purpose, run, submitPin, createAccount],
  );

  const onStartOver = useCallback(() => {
    setStage(purpose === 'signup' ? 'details' : 'email');
    setChallengeId(null);
    setError(null);
  }, [purpose]);

  const onCreateAccount = useCallback(() => {
    setStage('details');
    setNotice(null);
    setError(null);
  }, []);

  const onBackToSignIn = useCallback(() => {
    setStage('email');
    setPurpose('signin');
    setNotice(null);
    setError(null);
  }, []);

  return (
    <AuthScreen environment={environment}>
      {stage === 'details' ? (
        <CreateAccountForm
          key={stage}
          initial={details ?? { email }}
          pending={pending}
          error={error}
          notice={notice}
          submitLabel="Send pin"
          onSubmit={onDetails}
          secondaryLabel="I already have an account"
          onSecondary={onBackToSignIn}
        />
      ) : (
        <SignInForm
          // Remounts the form on step change so its inputs start empty.
          key={stage}
          stage={stage}
          email={email}
          purpose={purpose}
          pending={pending}
          error={error}
          onRequestPin={onRequestPin}
          onSubmitPin={onSubmitPin}
          onStartOver={onStartOver}
          onCreateAccount={onCreateAccount}
        />
      )}
    </AuthScreen>
  );
}
